/**
 * Conditional Loan Approval PDF Generator
 *
 * Generates a professional PDF that matches the Priority Loan Approval format:
 * - Company header with logo and license info
 * - Orange banner with "Conditional Loan Approval" title
 * - Structured sections: Lender, Subject Property, Borrowers, Loan Information
 * - Conditions tables organized by timing (PTD, PTF, AC)
 * - Signature section and page numbers
 */

import jsPDF from 'jspdf';
import {
  type ApprovalLetterData,
  type EncompassCondition,
  type BorrowerPairInfo,
  formatCurrency,
  formatPercent,
  formatDate
} from '@/types/approval-letter';
import type { ConditionTemplateInfo } from '@/types/condition-templates';

// Colors matching the reference document
const COLORS = {
  brandGreen: [22, 163, 74] as [number, number, number],   // Lendwise green #16A34A
  bannerText: [255, 255, 255] as [number, number, number], // White text on banner
  grayHeader: [220, 220, 220] as [number, number, number], // Gray section headers
  grayHeaderText: [0, 0, 0] as [number, number, number],   // Black text on gray
  tableRowAlt: [255, 253, 235] as [number, number, number],// Light cream for table rows
  black: [0, 0, 0] as [number, number, number],
  grayText: [100, 100, 100] as [number, number, number],
  white: [255, 255, 255] as [number, number, number],
  border: [180, 180, 180] as [number, number, number],
  greenCleared: [34, 139, 34] as [number, number, number], // For cleared status
  link: [0, 102, 204] as [number, number, number]         // Blue for hyperlinks
};

// Page dimensions (Letter size in points)
const PAGE = {
  width: 612,
  height: 792,
  marginLeft: 36,
  marginRight: 36,
  marginTop: 36,
  marginBottom: 50,
  get contentWidth() {
 return this.width - this.marginLeft - this.marginRight; 
}
};

// Column widths for conditions table
const CONDITION_COLS = {
  id: 40,  // Reduced - only 4 digits
  type: 70,
  conditionName: 120,
  get description() {
 return PAGE.contentWidth - this.id - this.type - this.conditionName; 
}
};

/**
 * Generate download URL for a condition document
 */
function getConditionDocumentUrl(externalId: string, loanGuid: string, borrowerPairId?: string): string {
  // Use Supabase functions URL - this creates a deep link that triggers download
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'https://mzyrtipixslfyzbjdpqd.supabase.co';
  let url = `${supabaseUrl}/functions/v1/generate-condition-document?externalId=${externalId}&loanGuid=${loanGuid}`;
  if (borrowerPairId) {
    url += `&borrowerPairId=${encodeURIComponent(borrowerPairId)}`;
  }
  return url;
}

/**
 * Options for PDF generation
 */
export interface GeneratePdfOptions {
  templateMap?: Map<string, ConditionTemplateInfo>;
}

/**
 * Main entry point - generates the approval letter PDF
 */
export async function generateApprovalLetterPdf(
  data: ApprovalLetterData,
  options: GeneratePdfOptions = {}
): Promise<Blob> {
  const { templateMap = new Map() } = options;

  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'pt',
    format: 'letter'
  });

  let y = PAGE.marginTop;

  // === PAGE 1: Header and Loan Summary ===
  y = drawCompanyHeader(doc, data, y);
  y = drawBanner(doc, y);
  y = drawLenderSection(doc, data, y);
  y = drawSubjectPropertyAndBorrowers(doc, data, y);
  y = drawLoanInformation(doc, data, y);

  // === CONDITIONS SECTIONS (Open conditions only) ===
  const openConditions = {
    ptd: data.conditions.ptd.filter(c => c.statusOpen),
    ptf: data.conditions.ptf.filter(c => c.statusOpen),
    ac: data.conditions.ac.filter(c => c.statusOpen)
  };

  // Prior To Docs
  if (openConditions.ptd.length > 0) {
    y = drawConditionsSection(doc, 'Prior To Docs', openConditions.ptd, y, data.loanGuid, templateMap, data.borrowerPairs);
  }

  // Prior To Funding
  if (openConditions.ptf.length > 0) {
    y = drawConditionsSection(doc, 'Prior To Funding', openConditions.ptf, y, data.loanGuid, templateMap, data.borrowerPairs);
  }

  // Prior To Closing (At Closing)
  if (openConditions.ac.length > 0) {
    y = drawConditionsSection(doc, 'Prior To Closing', openConditions.ac, y, data.loanGuid, templateMap, data.borrowerPairs);
  }

  // Signature section on last page
  drawSignatureSection(doc, y);

  // Add page numbers
  addPageNumbers(doc);

  return doc.output('blob');
}

/**
 * Draw company header with logo and license info
 */
function drawCompanyHeader(doc: jsPDF, data: ApprovalLetterData, startY: number): number {
  const y = startY;

  // Draw "Lendwise" as stylized text (logo substitute)
  doc.setFont('times', 'italic');
  doc.setFontSize(28);
  doc.setTextColor(...COLORS.black);
  doc.text('Lendwise', PAGE.marginLeft, y + 20);

  // "MORTGAGE" badge next to logo
  const lendwiseWidth = doc.getTextWidth('Lendwise');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setFillColor(180, 180, 180);
  doc.rect(PAGE.marginLeft + lendwiseWidth + 5, y + 5, 55, 18, 'F');
  doc.setTextColor(...COLORS.white);
  doc.text('MORTGAGE', PAGE.marginLeft + lendwiseWidth + 10, y + 17);

  // Loss Payee box (top right)
  const lossPayeeBoxWidth = 180;
  const lossPayeeBoxHeight = 55;
  const lossPayeeBoxX = PAGE.width - PAGE.marginRight - lossPayeeBoxWidth;
  const lossPayeeBoxY = y;

  doc.setDrawColor(...COLORS.black);
  doc.setLineWidth(0.5);
  doc.rect(lossPayeeBoxX, lossPayeeBoxY, lossPayeeBoxWidth, lossPayeeBoxHeight, 'S');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(...COLORS.black);
  doc.text('Loss Payee:', lossPayeeBoxX + 5, lossPayeeBoxY + 12);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.text('Lendwise Mortgage, Inc. ISAOA', lossPayeeBoxX + 5, lossPayeeBoxY + 23);
  doc.text('21800 Oxnard Street #220,', lossPayeeBoxX + 5, lossPayeeBoxY + 33);
  doc.text('Woodland Hills, CA 91367', lossPayeeBoxX + 5, lossPayeeBoxY + 43);
  doc.text(`Loan #: ${data.loanNumber}`, lossPayeeBoxX + 5, lossPayeeBoxY + 53);

  // License info text (below logo)
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.setTextColor(...COLORS.grayText);
  const licenseText = 'Lendwise Mortgage, Inc. (License No: 01308153, NMLS ID: 2702455) |';
  const stateLicense = data.lender.contactStateLicense || '01892824';
  const nmlsId = data.lender.contactNmls || '819586';
  const originatorText = `Originator: ${data.lender.contact} (License No: ${stateLicense}, NMLS ID: ${nmlsId})`;
  doc.text(licenseText, PAGE.marginLeft, y + 35);
  doc.text(originatorText, PAGE.marginLeft, y + 44);

  return y + 60;
}

/**
 * Draw the green banner with "Conditional Loan Approval" title
 */
function drawBanner(doc: jsPDF, startY: number): number {
  const bannerHeight = 28;

  // Green background (Lendwise brand color)
  doc.setFillColor(...COLORS.brandGreen);
  doc.rect(PAGE.marginLeft, startY, PAGE.contentWidth, bannerHeight, 'F');

  // White text centered
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.setTextColor(...COLORS.bannerText);
  doc.text('Conditional Loan Approval', PAGE.width / 2, startY + 19, { align: 'center' });

  return startY + bannerHeight + 10;
}

/**
 * Draw lender section with left (company) and right (file info) columns
 */
function drawLenderSection(doc: jsPDF, data: ApprovalLetterData, startY: number): number {
  let y = startY;
  const midPoint = PAGE.width / 2 - 20;

  // Section header with underline
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(...COLORS.black);
  doc.text('Lender', PAGE.marginLeft, y);
  doc.setDrawColor(...COLORS.black);
  doc.setLineWidth(0.5);
  doc.line(PAGE.marginLeft, y + 2, PAGE.marginLeft + 35, y + 2);
  y += 12;

  // Left column - Lender info
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);

  const lenderLines = [
    data.lender.name,
    data.lender.address,
    `${data.lender.city}, ${data.lender.state} ${data.lender.zip}`
  ];

  lenderLines.forEach(line => {
    doc.text(line, PAGE.marginLeft, y);
    y += 11;
  });

  // Phone and Fax on same line
  doc.text(`Phone: ${data.lender.phone}`, PAGE.marginLeft, y);
  doc.text(`Fax: ${data.lender.fax}`, PAGE.marginLeft + 120, y);

  // Right column - File info (starting from same Y as lender section)
  let rightY = startY + 12;
  const labelX = midPoint + 60;
  const valueX = midPoint + 120;

  const fileInfo = [
    ['Date:', formatDate(new Date().toISOString())],
    ['Loan Number:', data.loanNumber],
    // Use underwriting.loanOfficer (from field 317) with fallback to lender.contact
    ['Loan Officer:', data.underwriting.loanOfficer || data.lender.contact],
    ['Processor:', data.underwriting.processor],
    ['Underwriter:', data.underwriting.underwriter]
  ];

  fileInfo.forEach(([label, value]) => {
    doc.setFont('helvetica', 'normal');
    doc.text(label, labelX, rightY);
    doc.text(value || '', valueX, rightY);
    rightY += 11;
  });

  return Math.max(y, rightY) + 10;
}

/**
 * Draw Subject Property and Borrowers sections side by side with gray headers
 */
function drawSubjectPropertyAndBorrowers(doc: jsPDF, data: ApprovalLetterData, startY: number): number {
  let y = startY;
  const midPoint = PAGE.width / 2;
  const leftWidth = midPoint - PAGE.marginLeft - 10;
  const rightWidth = PAGE.width - PAGE.marginRight - midPoint - 10;
  const headerHeight = 14;

  // Left: Subject Property header (with border)
  doc.setFillColor(...COLORS.grayHeader);
  doc.setDrawColor(...COLORS.border);
  doc.setLineWidth(0.5);
  doc.rect(PAGE.marginLeft, y, leftWidth, headerHeight, 'FD');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(...COLORS.black);
  doc.text('Subject Property', PAGE.marginLeft + 3, y + 10);

  // Right: Borrowers header (with border)
  doc.setFillColor(...COLORS.grayHeader);
  doc.setDrawColor(...COLORS.border);
  doc.rect(midPoint, y, rightWidth, headerHeight, 'FD');
  doc.setTextColor(...COLORS.black);
  doc.text('Borrowers', midPoint + 3, y + 10);

  // Move Y past the header bars (increased spacing)
  y += headerHeight + 10;

  // Subject Property content
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);

  const propLabelX = PAGE.marginLeft + 3;
  const propValueX = PAGE.marginLeft + 50;
  let propY = y;

  doc.text('Address:', propLabelX, propY);
  doc.text(data.property.address, propValueX, propY);
  propY += 11;
  doc.text('', propLabelX, propY);
  doc.text(`${data.property.city}, ${data.property.state} ${data.property.zip}`, propValueX, propY);
  propY += 11;
  doc.text('Type:', propLabelX, propY);
  doc.text(data.property.propertyType || 'SFR', propValueX, propY);

  // Borrowers content (same starting Y as property content)
  let borrowerY = y;
  const borrLabelX = midPoint + 3;
  const borrValueX = midPoint + 65;

  doc.text('Borrower:', borrLabelX, borrowerY);
  doc.text(`${data.borrower.firstName} ${data.borrower.lastName}`, borrValueX, borrowerY);
  borrowerY += 11;

  doc.text('Co-Borrower:', borrLabelX, borrowerY);
  if (data.coBorrower) {
    doc.text(`${data.coBorrower.firstName} ${data.coBorrower.lastName}`, borrValueX, borrowerY);
  }

  return propY + 15;
}

/**
 * Draw Loan Information section with gray header and two-column layout
 */
function drawLoanInformation(doc: jsPDF, data: ApprovalLetterData, startY: number): number {
  let y = startY;
  const headerHeight = 14;

  // Gray header bar (with border)
  doc.setFillColor(...COLORS.grayHeader);
  doc.setDrawColor(...COLORS.border);
  doc.setLineWidth(0.5);
  doc.rect(PAGE.marginLeft, y, PAGE.contentWidth, headerHeight, 'FD');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(...COLORS.black);
  doc.text('Loan Information', PAGE.marginLeft + 3, y + 10);

  // Move Y past the header bar (increased spacing)
  y += headerHeight + 10;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);

  const col1LabelX = PAGE.marginLeft + 3;
  const col1ValueX = PAGE.marginLeft + 85;
  const col2LabelX = PAGE.width / 2 + 20;
  const col2ValueX = PAGE.width / 2 + 110;

  // Left column data
  const leftData = [
    ['Loan Program:', truncateText(data.loan.loanProgram, 30)],
    ['Mortgage Type:', data.loan.loanType],
    ['Loan Purpose:', data.loan.purpose],
    ['Lock Exp. Date:', formatDate(data.lock.lockExpires) || 'N/A'],
    ['Occupancy:', data.loan.occupancy],
    ['Note Rate:', `${formatPercent(data.lock.noteRate)}    Qualifying Rate: ${formatPercent(data.lock.qualRate)}`],
    ['Term:', `${data.loan.term} months`],
    ['DTI:', `${data.loan.backRatio?.toFixed(2) || '0.00'}    DSCR: `],
    ['Doc Type:', data.loan.docType || 'Full Doc']
  ];

  // Right column data
  const rightData = [
    ['Sales Price/Value:', formatCurrency(data.loan.salesPrice || data.loan.appraisedValue)],
    ['Appraised Value:', formatCurrency(data.loan.appraisedValue)],
    ['Base Loan Amount:', formatCurrency(data.loan.totalLoanAmount)],
    ['Total Loan Amount:', formatCurrency(data.loan.totalLoanAmount)],
    ['LTV:', formatPercent(data.loan.ltv)],
    ['CLTV:', formatPercent(data.loan.cltv)],
    ['HCLTV:', formatPercent(data.loan.cltv)] // Usually same as CLTV unless HELOC
  ];

  // Draw left column
  let leftY = y;
  leftData.forEach(([label, value]) => {
    doc.text(label, col1LabelX, leftY);
    doc.text(value, col1ValueX, leftY);
    leftY += 12;
  });

  // Draw right column
  let rightY = y;
  rightData.forEach(([label, value]) => {
    doc.text(label, col2LabelX, rightY);
    doc.text(value, col2ValueX, rightY);
    rightY += 12;
  });

  return Math.max(leftY, rightY) + 10;
}

/**
 * Draw a conditions section with table format
 */
function drawConditionsSection(
  doc: jsPDF,
  title: string,
  conditions: EncompassCondition[],
  startY: number,
  loanGuid: string,
  templateMap: Map<string, ConditionTemplateInfo>,
  borrowerPairs?: BorrowerPairInfo[]
): number {
  let y = startY;

  // Check for page break before section
  if (y > PAGE.height - 150) {
    doc.addPage();
    y = PAGE.marginTop;
  }

  // Section title (bold, underlined)
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(...COLORS.black);
  doc.text(title, PAGE.marginLeft, y);
  y += 5;

  // Draw table header
  y = drawConditionTableHeader(doc, y);

  // Draw condition rows — expand template-backed conditions into per-pair rows when multi-pair
  const hasMultiplePairs = borrowerPairs && borrowerPairs.length > 1;
  let rowIndex = 0;
  conditions.forEach((condition) => {
    const hasTemplate = templateMap.has(condition.externalId);

    if (hasTemplate && hasMultiplePairs) {
      // One row per borrower pair
      for (const pair of borrowerPairs) {
        y = drawConditionRow(doc, condition, y, rowIndex % 2 === 0, loanGuid, templateMap, pair);
        rowIndex++;
      }
    } else {
      y = drawConditionRow(doc, condition, y, rowIndex % 2 === 0, loanGuid, templateMap);
      rowIndex++;
    }
  });

  // Bottom border of table
  doc.setDrawColor(...COLORS.border);
  doc.setLineWidth(0.5);
  doc.line(PAGE.marginLeft, y, PAGE.marginLeft + PAGE.contentWidth, y);

  return y + 15;
}

/**
 * Draw condition table header row
 */
function drawConditionTableHeader(doc: jsPDF, startY: number): number {
  const y = startY;
  const rowHeight = 14;

  // Header background
  doc.setFillColor(...COLORS.grayHeader);
  doc.rect(PAGE.marginLeft, y, PAGE.contentWidth, rowHeight, 'F');

  // Border
  doc.setDrawColor(...COLORS.border);
  doc.setLineWidth(0.5);
  doc.rect(PAGE.marginLeft, y, PAGE.contentWidth, rowHeight, 'S');

  // Column headers
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(...COLORS.black);

  const idX = PAGE.marginLeft + 3;
  const typeX = PAGE.marginLeft + CONDITION_COLS.id + 3;
  const conditionNameX = PAGE.marginLeft + CONDITION_COLS.id + CONDITION_COLS.type + 3;
  const descriptionX = PAGE.marginLeft + CONDITION_COLS.id + CONDITION_COLS.type + CONDITION_COLS.conditionName + 3;

  doc.text('ID', idX, y + 10);
  doc.text('Type', typeX, y + 10);
  doc.text('Condition', conditionNameX, y + 10);
  doc.text('Description', descriptionX, y + 10);

  // Vertical lines for columns
  doc.line(PAGE.marginLeft + CONDITION_COLS.id, y, PAGE.marginLeft + CONDITION_COLS.id, y + rowHeight);
  doc.line(PAGE.marginLeft + CONDITION_COLS.id + CONDITION_COLS.type, y,
           PAGE.marginLeft + CONDITION_COLS.id + CONDITION_COLS.type, y + rowHeight);
  doc.line(PAGE.marginLeft + CONDITION_COLS.id + CONDITION_COLS.type + CONDITION_COLS.conditionName, y,
           PAGE.marginLeft + CONDITION_COLS.id + CONDITION_COLS.type + CONDITION_COLS.conditionName, y + rowHeight);

  return y + rowHeight;
}

/**
 * Draw a single condition row
 */
function drawConditionRow(
  doc: jsPDF,
  condition: EncompassCondition,
  startY: number,
  isAlternate: boolean,
  loanGuid: string,
  templateMap: Map<string, ConditionTemplateInfo>,
  pair?: BorrowerPairInfo
): number {
  // Calculate row height based on text wrapping for description
  const descriptionText = condition.externalDescription || condition.internalDescription || '';
  const maxDescriptionWidth = CONDITION_COLS.description - 10;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  const wrappedLines = doc.splitTextToSize(descriptionText, maxDescriptionWidth);
  const rowHeight = Math.max(16, wrappedLines.length * 10 + 6);

  // Check for page break
  if (startY + rowHeight > PAGE.height - PAGE.marginBottom) {
    doc.addPage();
    startY = PAGE.marginTop;
    // Redraw header on new page
    startY = drawConditionTableHeader(doc, startY);
  }

  const y = startY;

  // Row background
  if (isAlternate) {
    doc.setFillColor(...COLORS.tableRowAlt);
    doc.rect(PAGE.marginLeft, y, PAGE.contentWidth, rowHeight, 'F');
  }

  // Row border
  doc.setDrawColor(...COLORS.border);
  doc.setLineWidth(0.3);
  doc.rect(PAGE.marginLeft, y, PAGE.contentWidth, rowHeight, 'S');

  // Vertical lines for all 4 columns
  doc.line(PAGE.marginLeft + CONDITION_COLS.id, y,
           PAGE.marginLeft + CONDITION_COLS.id, y + rowHeight);
  doc.line(PAGE.marginLeft + CONDITION_COLS.id + CONDITION_COLS.type, y,
           PAGE.marginLeft + CONDITION_COLS.id + CONDITION_COLS.type, y + rowHeight);
  doc.line(PAGE.marginLeft + CONDITION_COLS.id + CONDITION_COLS.type + CONDITION_COLS.conditionName, y,
           PAGE.marginLeft + CONDITION_COLS.id + CONDITION_COLS.type + CONDITION_COLS.conditionName, y + rowHeight);

  // Column positions
  const idX = PAGE.marginLeft + 3;
  const typeX = PAGE.marginLeft + CONDITION_COLS.id + 3;
  const conditionNameX = PAGE.marginLeft + CONDITION_COLS.id + CONDITION_COLS.type + 3;
  const descriptionX = PAGE.marginLeft + CONDITION_COLS.id + CONDITION_COLS.type + CONDITION_COLS.conditionName + 3;

  doc.setFontSize(8);

  // Check if this condition has a template (determine early for all columns)
  const hasTemplate = templateMap.has(condition.externalId);
  const downloadUrl = hasTemplate && loanGuid
    ? getConditionDocumentUrl(condition.externalId, loanGuid, pair?.applicationId)
    : null;

  // ID column - show external ID (blue link if template exists)
  if (downloadUrl) {
    doc.setTextColor(...COLORS.link);
    doc.textWithLink(condition.externalId || '', idX, y + 10, { url: downloadUrl });
  } else {
    doc.setTextColor(...COLORS.black);
    doc.text(condition.externalId || '', idX, y + 10);
  }

  // Type column - show category (blue link if template exists)
  if (downloadUrl) {
    doc.setTextColor(...COLORS.link);
    doc.textWithLink(condition.category || '', typeX, y + 10, { url: downloadUrl });
  } else {
    doc.setTextColor(...COLORS.black);
    doc.text(condition.category || '', typeX, y + 10);
  }

  // Condition column - show title (with hyperlink if template exists)
  // Append pair label when rendering per-pair rows
  const conditionDisplayTitle = pair
    ? `${condition.title || ''} (${pair.displayLabel})`
    : (condition.title || '');
  const maxTitleWidth = CONDITION_COLS.conditionName - 6;
  const titleLines = doc.splitTextToSize(conditionDisplayTitle, maxTitleWidth);
  const titleText = titleLines[0] || '';

  if (downloadUrl) {
    // Render title as a clickable blue link
    doc.setTextColor(...COLORS.link);
    doc.textWithLink(titleText, conditionNameX, y + 10, { url: downloadUrl });
  } else {
    // Regular text
    doc.setTextColor(...COLORS.black);
    doc.text(titleText, conditionNameX, y + 10);
  }

  // Description column - wrapped text (also blue and clickable if template exists)
  doc.setFont('helvetica', 'normal');
  let textY = y + 10;
  if (downloadUrl) {
    // Render description as blue clickable text
    doc.setTextColor(...COLORS.link);
    wrappedLines.forEach((line: string) => {
      doc.textWithLink(line, descriptionX, textY, { url: downloadUrl });
      textY += 10;
    });
    doc.setTextColor(...COLORS.black);
  } else {
    // Regular black text
    doc.setTextColor(...COLORS.black);
    wrappedLines.forEach((line: string) => {
      doc.text(line, descriptionX, textY);
      textY += 10;
    });
  }

  return y + rowHeight;
}

/**
 * Draw signature section at bottom
 */
function drawSignatureSection(doc: jsPDF, startY: number): void {
  // Ensure we have space, otherwise add page
  if (startY > PAGE.height - 100) {
    doc.addPage();
    startY = PAGE.marginTop + 300; // Leave space for any final conditions
  }

  const y = PAGE.height - 80;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...COLORS.black);

  // Signature line
  doc.text('Signature:', PAGE.marginLeft, y);
  doc.setDrawColor(...COLORS.black);
  doc.setLineWidth(0.5);
  doc.line(PAGE.marginLeft + 55, y, PAGE.marginLeft + 180, y);

  // Date line
  doc.text('Date:', PAGE.width / 2 + 50, y);
  doc.line(PAGE.width / 2 + 80, y, PAGE.width / 2 + 180, y);
}

/**
 * Add page numbers to all pages
 */
function addPageNumbers(doc: jsPDF): void {
  const totalPages = doc.getNumberOfPages();

  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(...COLORS.grayText);
    doc.text(
      `Page ${i} of ${totalPages}`,
      PAGE.width / 2,
      PAGE.height - 25,
      { align: 'center' }
    );
  }
}

/**
 * Truncate text to fit in a column
 */
function truncateText(text: string, maxLength: number): string {
  if (!text) {
return '';
}
  if (text.length <= maxLength) {
return text;
}
  return `${text.substring(0, maxLength - 3)  }...`;
}

/**
 * Download the PDF directly
 */
export async function downloadApprovalLetterPdf(
  data: ApprovalLetterData,
  filenameOrOptions?: string | GeneratePdfOptions,
  filename?: string
): Promise<void> {
  // Handle backwards compatibility - first arg after data can be filename or options
  let options: GeneratePdfOptions = {};
  let finalFilename: string | undefined;

  if (typeof filenameOrOptions === 'string') {
    finalFilename = filenameOrOptions;
  } else if (filenameOrOptions) {
    options = filenameOrOptions;
    finalFilename = filename;
  }

  const blob = await generateApprovalLetterPdf(data, options);
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = finalFilename || `Conditional_Approval_${data.loanNumber}_${new Date().toISOString().split('T')[0]}.pdf`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
